import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { Star } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import DisciplineSwitch from "../components/DisciplineSwitch";
import PlotReveal from "../components/PlotReveal";
import InstagramFeed from "../components/InstagramFeed";
import WavyEnd from "../components/WavyEnd";
import ServicesShowcase from "../components/ServicesShowcase";

const DISCIPLINES = ["Residential Architecture", "Residential Interiors", "Commercial Architecture", "Commercial Fit-outs"];

/** Hero focal helpers — read hero_focal_x/y/scale on project doc */
function heroStyle(project) {
  const fx = project?.hero_focal_x ?? 50;
  const fy = project?.hero_focal_y ?? 50;
  const scale = (project?.hero_scale ?? 100) / 100;
  return {
    objectFit: "cover",
    objectPosition: `${fx}% ${fy}%`,
    transform: `scale(${scale})`,
    transformOrigin: `${fx}% ${fy}%`,
  };
}

export default function Home() {
  const navigate = useNavigate();
  const { scrollY } = useScroll();
  const heroImgY = useTransform(scrollY, [0, 800], [0, 80]);
  const heroTextY = useTransform(scrollY, [0, 800], [0, -40]);

  const { data: featured = [] } = useQuery({
    queryKey: ["projects", "featured"],
    queryFn: async () => (await api.get("/projects", { params: { featured: true } })).data,
  });
  const { data: recent = [] } = useQuery({
    queryKey: ["projects", "recent"],
    queryFn: async () => (await api.get("/projects")).data,
  });

  const heroProject = featured[0] || recent[0];
  const heroImg = heroProject?.gallery?.find((g) => g.id === heroProject?.hero_image_id) || heroProject?.gallery?.[0];
  const isVideo = heroProject?.hero_media_type === "video" && heroProject?.hero_video_path;

  // Home "Selected work" list — admin-controlled via the featured star toggle in /admin.
  // Show ALL projects marked featured (already sorted by sort_order asc on the backend).
  // Fallback: if no project is featured, show the 4 most recent so the Home page never looks empty.
  const selected = featured.length ? featured : recent.slice(0, 4);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="home-page" className="-mt-14 md:-mt-16">
      {/* ---------- STARTER PICTURE (FIRST) ---------- */}
      <section className="relative w-full h-screen overflow-hidden bg-ink">
        {/* Render hero media only once we actually have a project. Prevents
            the "flicker" where an old fallback image + placeholder title
            would briefly render before the real featured project loaded. */}
        {heroProject && (
          <motion.div style={{ y: heroImgY }} className="absolute inset-0"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            {isVideo ? (
              <video
                src={fileUrl(heroProject.hero_video_path)}
                autoPlay muted loop playsInline
                className="w-full h-full"
                style={heroStyle(heroProject)}
              />
            ) : heroImg ? (
              <img
                src={fileUrl(heroImg.variants.full)}
                alt={heroProject.title}
                className="w-full h-full"
                style={heroStyle(heroProject)}
              />
            ) : null}
            <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-ink/10 to-ink/85 pointer-events-none" />
          </motion.div>
        )}

        <motion.div style={{ y: heroTextY }} className="relative z-10 container-w h-full flex flex-col justify-end pb-16 md:pb-24 text-paper">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
            <div className="md:col-span-8">
              {/* Only render label + title once heroProject is loaded — no
                  placeholder text that would flicker to the real title. */}
              {heroProject && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.55, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                >
                  <div className="mono-label text-white mb-4 drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">Featured — {heroProject.discipline || "Project"}</div>
                  <Link
                    to={`/work/${heroProject.slug}`}
                    data-testid="hero-title-link"
                    className="group inline-flex items-start gap-3 md:gap-5 relative"
                  >
                    <h1 className="font-display italic text-5xl sm:text-6xl md:text-8xl leading-[0.9] tracking-tight text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.55)] transition-colors duration-500 group-hover:text-greige">
                      {heroProject.title}
                    </h1>
                    <motion.span
                      aria-hidden
                      className="mt-2 md:mt-4 text-greige opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                      animate={{ rotate: [0, 20, -12, 0], scale: [1, 1.25, 0.9, 1.1] }}
                      transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
                    >
                      <Star size={36} strokeWidth={1.2} fill="currentColor" />
                    </motion.span>
                  </Link>
                  {heroProject.summary && (
                    <div className="mt-6 max-w-xl">
                      <p className="text-white/85 text-base md:text-lg leading-relaxed line-clamp-3 drop-shadow-[0_1px_4px_rgba(0,0,0,0.5)]">
                        {heroProject.summary}
                      </p>
                      <WavyEnd className="text-white/80 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]" width={140} />
                    </div>
                  )}
                </motion.div>
              )}
            </div>
            <div className="md:col-span-4 flex md:justify-end items-end gap-6">
              {heroProject && (
                <Link
                  to={`/work/${heroProject.slug}`}
                  data-testid="hero-view-project"
                  className="mono-label link-underline text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.55)]"
                >
                  View project →
                </Link>
              )}
              <Link to="/work" data-testid="hero-all-work" className="mono-label link-underline text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.55)]">
                All work →
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Vertical mono ticker on the left edge — contained 56px column, rotated content stays put */}
        <div className="hidden md:flex absolute left-0 top-0 bottom-0 w-14 items-center justify-center pointer-events-none z-10">
          <div
            className="flex items-center gap-4 whitespace-nowrap"
            style={{ transform: "rotate(-90deg)", transformOrigin: "center center" }}
          >
            <span className="inline-block w-8 border-t border-paper/40" />
            <span className="mono-label text-paper/70">The White Project Co — Sydney · Est. 2021</span>
            <span className="inline-block w-8 border-t border-paper/40" />
          </div>
        </div>

        {/* Bottom scroll cue */}
        <div className="absolute bottom-6 right-6 mono-label text-paper/70">Scroll ↓</div>
      </section>

      {/* ---------- DISCIPLINE SWITCH SECTION (SECOND) ---------- */}
      <section className="py-32 md:py-48 container-w relative">
        {/* Artistic background wordmark — huge, faded, offset */}
        <div aria-hidden className="pointer-events-none select-none absolute -right-6 top-1/3 opacity-[0.12]">
          <img src="/brand/typography-greige.png" alt="" className="w-[900px] max-w-none" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 relative">
          <div className="md:col-span-8">
            <div className="mono-label mb-8">Est. Sydney — Practice</div>
            <h2 data-testid="hero-title" className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[7.5rem] leading-[0.95] tracking-tight">
              <span className="block">A studio for</span>
              <DisciplineSwitch />
              <span className="block italic">architecture.</span>
            </h2>
            <p className="mt-12 max-w-xl text-base md:text-lg text-ash leading-relaxed">
              We design considered spaces across residential and commercial work — with a focus on proportion, material honesty, and the discipline of restraint.
            </p>
            <div className="mt-10 flex items-center gap-6 flex-wrap">
              <Link to="/work" data-testid="hero-cta-work" className="mono-label link-underline">View selected work →</Link>
              <Link to="/contact" data-testid="hero-cta-contact" className="mono-label link-underline">Enquire →</Link>
            </div>
          </div>
          <div className="md:col-span-4 md:pl-12 md:border-l md:border-ink/10 pt-8 md:pt-24">
            <div className="mono-label mb-4">Disciplines</div>
            <ul className="space-y-3">
              {DISCIPLINES.map((d, i) => (
                <li key={d} className="flex items-baseline gap-4">
                  <span className="mono-label">0{i + 1}</span>
                  <Link
                    to={`/work?discipline=${encodeURIComponent(d)}`}
                    className="font-display italic text-lg md:text-xl link-underline"
                    data-testid={`discipline-${i}`}
                  >
                    {d}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ---------- SELECTED WORK ---------- */}
      <section className="container-w mb-32 md:mb-48 relative">
        <div className="flex items-baseline justify-between mb-12 md:mb-20">
          <div>
            <div className="mono-label mb-2">Selected work</div>
            <h2 className="font-display italic text-4xl md:text-6xl tracking-tight">A quiet index.</h2>
          </div>
          <Link to="/work" className="mono-label link-underline hidden md:inline" data-testid="all-work-link">All projects →</Link>
        </div>
        <div className="flex flex-col gap-y-24 md:gap-y-40">
          {selected.filter((p) => (p.gallery?.length || 0) > 0).map((p, idx) => {
            const img = p.gallery?.find((g) => g.id === p.hero_image_id) || p.gallery?.[0];
            const src = fileUrl(img.variants.grid);
            // Editorial column arrangement — alternate offset (cycles every 4 for any list length)
            const cyc = idx % 4;
            const imageCol = cyc === 0 ? "md:col-span-8 md:col-start-1"
              : cyc === 1 ? "md:col-span-5 md:col-start-8"
              : cyc === 2 ? "md:col-span-6 md:col-start-2"
              : "md:col-span-4 md:col-start-9";
            const descCol = cyc === 0 ? "md:col-span-3 md:col-start-10"
              : cyc === 1 ? "md:col-span-5 md:col-start-2 md:row-start-1"
              : cyc === 2 ? "md:col-span-4 md:col-start-9"
              : "md:col-span-5 md:col-start-2 md:row-start-1";
            // Size the image plate to the image's own aspect ratio — no crop.
            // Cap the height so ultra-tall portraits don't dominate the row.
            const iw = img?.width || 4;
            const ih = img?.height || 3;
            const ar = iw / ih;
            const CAP = cyc === 0 ? "72vh" : cyc === 3 ? "58vh" : "68vh";
            const plateStyle = {
              aspectRatio: `${iw} / ${ih}`,
              maxHeight: CAP,
              width: `min(100%, calc(${CAP} * ${ar}))`,
            };
            return (
              <div key={p.id} className="grid grid-cols-1 md:grid-cols-12 md:gap-x-8 items-end">
                <Link to={`/work/${p.slug}`} className={`group block ${imageCol}`} data-testid={`selected-${idx}`}>
                  <PlotReveal>
                    <div className="border border-greige/60 p-2 md:p-3 bg-paper" style={plateStyle}>
                      <div className="overflow-hidden w-full h-full">
                        <motion.img
                          src={src}
                          alt={p.title}
                          className="w-full h-full object-cover block"
                          whileHover={{ scale: 1.03 }}
                          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
                        />
                      </div>
                    </div>
                  </PlotReveal>
                  <div className="mt-4 flex justify-between items-baseline">
                    <span className="font-display italic text-2xl">{p.title}</span>
                    <span className="mono-label">{p.discipline}</span>
                  </div>
                  <div className="mono-label mt-1 text-ash">{p.location} · {p.year}</div>
                </Link>
                <aside
                  className={`mt-8 md:mt-0 ${descCol} text-ink`}
                  data-testid={`selected-desc-${idx}`}
                >
                  <div className="mono-label text-marine mb-3">— 0{idx + 1} · Project note</div>
                  <p className="text-ash text-sm md:text-base leading-relaxed">
                    {p.summary || `A ${p.discipline?.toLowerCase() || "considered"} project in ${p.location || "Sydney"} — built on proportion, material honesty and daylight as a material.`}
                  </p>
                  <WavyEnd className="text-marine" width={140} />
                  <Link
                    to={`/work/${p.slug}`}
                    className="mono-label link-underline mt-4 inline-block"
                    data-testid={`selected-desc-cta-${idx}`}
                  >
                    Read the project →
                  </Link>
                </aside>
              </div>
            );
          })}
        </div>
      </section>

      {/* ---------- SERVICES / WHAT WE OFFER (interactive showcase) ---------- */}
      <ServicesShowcase />

      {/* ---------- LEGACY services grid (kept for SEO snippets but hidden) --------- */}
      <section
        id="services-grid"
        aria-labelledby="services-heading"
        className="container-w py-32 md:py-40 border-t border-ink/10 relative hidden"
        data-testid="services-section"
      >
        <div aria-hidden className="pointer-events-none select-none absolute -left-6 top-24 rotate-[-90deg] origin-left mono-label text-ash/40">
          — 03 / Services
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start mb-16 md:mb-20">
          <div className="md:col-span-5">
            <div className="mono-label mb-4">What we offer</div>
            <h2 id="services-heading" className="font-display italic text-4xl md:text-6xl leading-[0.95] tracking-tight">
              Services,<br />end to end.
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <p className="text-ash text-base md:text-lg leading-relaxed max-w-[52ch]">
              From the first sketch to the last certificate — we hold the line from planning through documentation. Choose a single piece or the full arc.
            </p>
          </div>
        </div>

        <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10 border border-ink/10" itemScope itemType="https://schema.org/OfferCatalog">
          {[].map(() => null)}
          <li className="bg-paper p-8 md:p-10 flex flex-col justify-between gap-6 border-t md:border-t-0 md:border-l border-ink/10 hidden lg:flex">
            <div>
              <div className="mono-label text-marine mb-4">— Not sure?</div>
              <p className="font-display italic text-2xl md:text-3xl leading-tight tracking-tight">
                Tell us the site and the ambition — we&rsquo;ll map the pathway.
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="services-enquire"
              className="mono-label link-underline self-start"
            >
              Start an enquiry →
            </Link>
          </li>
        </ul>
      </section>

      {/* ---------- INSTAGRAM FEED (between selected work & studio statement) ---------- */}
      <InstagramFeed />

      {/* ---------- STUDIO STATEMENT ---------- */}
      <section className="py-32 md:py-40 border-y border-ink/10 relative">
        <div aria-hidden className="pointer-events-none select-none absolute -left-24 md:left-6 top-6 rotate-[-90deg] origin-top-left">
          <div className="mono-label text-ash/60">— Statement / 03</div>
        </div>
        <div className="container-w text-center flex flex-col items-center">
          <div className="mono-label mb-8">Studio statement</div>
          <p className="font-display italic text-3xl md:text-5xl leading-[1.1] max-w-[52ch]">
            We build with the discipline of restraint — where daylight is a material, proportion is the argument, and every line has been earned.
          </p>
          <WavyEnd className="text-ink mt-6" width={220} />
          <Link to="/studio" className="mono-label link-underline mt-10" data-testid="studio-link">Read the practice statement →</Link>
        </div>
      </section>

      {/* ---------- CONTACT PROMPT ---------- */}
      <section className="container-w py-32 md:py-40 relative">
        <div aria-hidden className="pointer-events-none select-none absolute -left-6 top-1/2 rotate-[-90deg] origin-left mono-label text-ash/40">
          — 04 / Enquire
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-6">
            <div className="mono-label mb-4">A brief conversation</div>
            <h2 className="font-display italic text-4xl md:text-6xl tracking-tight">Considering<br />a project?</h2>
          </div>
          <div className="md:col-span-6 flex md:items-end">
            <div>
              <p className="max-w-md text-ash text-base md:text-lg mb-4">
                We accept a small number of commissions each year. Send us the outline and we&rsquo;ll respond within a week.
              </p>
              <WavyEnd className="text-ash mb-6" width={140} />
              <Link to="/contact" className="inline-block mono-label px-8 py-4 border border-ink/20 text-marine hover:bg-marine hover:text-paper transition-colors duration-500" data-testid="contact-cta">
                Start an enquiry →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
